"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { stegaClean } from "@sanity/client/stega";

import { PageAdLongSchema } from "@/sanity/pages/PageAdLongTemplate";

import dots from "@/public/vsl/dotsSvg.svg";
import tab from "@/public/ad-short/tab.svg";
import flag from "@/public/ad-short/flag.svg";

// card accent colors from Figma, cycled when an item has no explicit color
const ACCENT_COLORS = ["#F9E0B9", "#D9F9B9", "#E9B9F9", "#B9F0F9", "#B9C0F9"];

export default function MethodeSection({ data, className }: { data: PageAdLongSchema; className?: string }) {
  return (
    <section id="methode" className={cn("relative overflow-clip bg-[#0A1A1D]", className)}>
      {/* side lines flanking the content */}
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[8.85%]"
        orientation="vertical"
        style="light"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[8.85%]"
        orientation="vertical"
        style="light"
      />

      <Image
        src={dots}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[min(119.875rem,90%)] select-none"
      />

      <div className="relative z-10 mx-auto w-[min(150.125rem,100%)] px-[2rem] sm:px-[3rem] md:px-[5rem] lg:px-0 pt-[15rem] pb-[13.75rem]">
        {/* header */}
        <div className="flex flex-col items-center text-center">
          {data?.methodeChip?.text && (
            <Chip
              className="mb-[2.375rem] [&_p]:uppercase [&_p]:font-geistmono [&_p]:text-[#202223]"
              image={data?.methodeChip?.icon}
              text={data?.methodeChip?.text}
            />
          )}

          {data?.methodeTitle && (
            <div className="richtext richtext-center w-[min(122.375rem,100%)] prose-em:font-ivy prose-em:text-[#D9F9B9] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-white prose-headings:text-[4.5rem] sm:prose-headings:text-[5.5rem] lg:prose-headings:text-[6.5rem] prose-headings:leading-none prose-headings:text-balance">
              <PortableText
                value={data.methodeTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.methodeText && (
            <div className="richtext richtext-center mt-[2.375rem] w-[min(105.5rem,100%)] prose-p:text-white/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.5]">
              <PortableText
                value={data.methodeText}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* cards */}
        {data?.methodeItems && data.methodeItems.length > 0 && (
          <div className="flex flex-col mt-[7rem] gap-[9rem]">
            {data.methodeItems.map((item, index) => {
              const accent = item?.color || ACCENT_COLORS[index % ACCENT_COLORS.length];
              const isVideo = stegaClean(item?.mediaType) === "video";
              const hasVideo =
                item?.video?.muxVideo?.playbackId || item?.video?.embedUrl;

              return (
                <div
                  key={index}
                  /*
                    Each card sticks at the same offset inside the list, so later cards
                    scroll up and stack over the earlier ones. Desktop only — on mobile a
                    card is taller than the viewport, which sticky would crop.
                  */
                  className="flex items-stretch lg:sticky lg:top-[12rem]">
                  {/* left rail: accent line + number tab */}
                  <div className="relative w-[3.5rem] shrink-0">
                    <div
                      className="absolute top-0 right-0 h-full w-[2px]"
                      style={{ backgroundColor: accent }}
                    />
                    {/*
                      The rail is transparent apart from this tab, and each card offsets
                      its own tab by one tab-height per index. As the cards stack the tabs
                      therefore pile up below one another instead of overlapping.
                    */}
                    <div
                      className="absolute left-0 top-0 lg:top-[var(--tab-offset)] h-[5.5rem] w-[3.25rem]"
                      style={
                        { "--tab-offset": `${index * 5.5}rem` } as React.CSSProperties
                      }>
                      <div
                        className="absolute top-1/2 left-1/2 rotate-90 -translate-x-1/2 -translate-y-1/2 h-[3.25rem] w-[5.5rem] -scale-y-100"
                        style={{
                          backgroundColor: accent,
                          maskImage: `url(${tab.src})`,
                          WebkitMaskImage: `url(${tab.src})`,
                          maskSize: "100% 100%",
                          WebkitMaskSize: "100% 100%",
                        }}
                      />
                      <span className="flex absolute inset-0 justify-center items-center font-medium leading-none text-black font-geistmono text-[2rem]">
                        {index + 1}
                      </span>
                    </div>
                  </div>

                  {/* card body */}
                  <div className="flex flex-col flex-1 justify-between lg:flex-row gap-[4rem] rounded-r-[0.75rem] bg-[#112628] p-[1.5rem] pl-[3rem] lg:pl-[6rem]">
                    {/* text column */}
                    <div className="flex flex-col w-full lg:justify-between gap-[3rem] py-[4.5rem] lg:w-[64.25rem]">
                      <div className="flex flex-col gap-[3rem]">
                        {/* mb-0 cancels the [data-theme="new"] :where(h3) margin; width matches the fixed 396px text box in Figma so long titles break the same way */}
                        {item?.title && (
                          <h3 className="mb-0 font-medium text-white font-geist text-[4rem] leading-[1.14] lg:text-[5rem] w-[min(49.5rem,100%)] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                            {item.title}
                          </h3>
                        )}
                        {item?.text && (
                          <p className="font-geist text-[2.25rem] leading-[1.4] text-white/60 w-[min(58.125rem,100%)] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic] m-0">
                            {item.text}
                          </p>
                        )}
                        <div className="w-full border-t border-white/10" />
                      </div>

                      <div className="flex flex-col gap-[3rem]">
                        {item?.listLabel && (
                          <p className="m-0 uppercase font-geistmono text-[2rem] leading-[1.4] text-white/50 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                            {item.listLabel}
                          </p>
                        )}

                        {item?.list && item.list.length > 0 && (
                          <ul className="flex flex-col gap-[3rem]">
                            {item.list.map((entry, entryIndex) => (
                              <li
                                key={entryIndex}
                                className="flex items-center gap-[1rem]">
                                <span
                                  className="h-[1.5rem] w-[1.5rem] shrink-0"
                                  style={{
                                    backgroundColor: accent,
                                    maskImage: `url(${flag.src})`,
                                    WebkitMaskImage: `url(${flag.src})`,
                                    maskSize: "100% 100%",
                                    WebkitMaskSize: "100% 100%",
                                  }}
                                />
                                <span className="text-white font-geist text-[2.25rem] leading-[1.5] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
                                  {entry}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {(item?.infoLabel || item?.infoText) && (
                          <div
                            className="flex items-stretch w-full"
                            style={{ backgroundColor: `${accent}0D` }}>
                            <div
                              className="w-[2px] shrink-0"
                              style={{ backgroundColor: accent }}
                            />
                            <div className="flex flex-col flex-1 gap-[1.5rem] px-[1.5rem] py-[2rem]">
                              {item?.infoLabel && (
                                <p
                                  className="m-0 font-medium uppercase font-geistmono text-[1.75rem] leading-[1.5] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]"
                                  style={{ color: accent }}>
                                  {item.infoLabel}
                                </p>
                              )}
                              {item?.infoText && (
                                <p
                                  className="m-0 font-geist text-[2rem] leading-[1.3] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]"
                                  style={{ color: `${accent}80` }}>
                                  {item.infoText}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* media column — image or video, per the item's toggle */}
                    {isVideo
                      ? hasVideo && (
                          <div className="relative w-full shrink-0 aspect-[486/531] overflow-clip rounded-[0.5rem] lg:w-[60.75rem]">
                            <HLSVideoPlayer
                              source={item.video}
                              autoPlay
                              muted
                              loop
                              playsInline
                              poster={item?.image?.src}
                              className="absolute left-0 top-0 !h-full !w-full object-cover"
                            />
                          </div>
                        )
                      : item?.image?.src && (
                          <div className="relative w-full shrink-0 aspect-[486/531] overflow-clip rounded-[0.5rem] lg:w-[60.75rem]">
                            <Image
                              className="absolute left-0 top-0 !h-full !w-full object-cover"
                              src={item.image}
                              alt=""
                            />
                          </div>
                        )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        {data?.methodeCTA && (
          <div className="flex justify-center mt-[7rem]">
            <CTACornerButton data={data.methodeCTA} />
          </div>
        )}
      </div>
    </section>
  );
}
